/* ===========================================================================
 * Mobile Sauna — Booking widget  (Phase 1: function-only, mock data)
 * ---------------------------------------------------------------------------
 * This is the storefront widget described in the project plan, Sections 6 & 8.
 * Launch: a single schedule view — one week at a time, with a next-week button
 * advancing through the one-month booking window (single location, so there is no
 * location picker or view toggle). A shared booking drawer handles ticket
 * selection (single spot or Group Booking) and the cart hand-off.
 *
 * PHASE STATUS
 *   Phase 1: look/feel/interaction on mock data.  Phase 2: reads real sessions
 *   from per-location metafields (falls back to mock if absent).  Phase 3: "Add
 *   to cart" POSTs to /cart/add.js with the session in line-item properties; the
 *   Cloudflare Worker decrements seats on the order webhook — see addToCart().
 *
 * HOW DATA FLOWS IN (so Phase 2 can swap mock -> real with no rewrite)
 *   The Liquid section renders:
 *     <div data-sauna-booking>
 *       <script type="application/json" data-sauna-config> {...} </script>
 *       <script type="application/json" data-sauna-sessions> [...] </script>
 *     </div>
 *   - config  : locations, times, capacity, prices, window, default view.
 *   - sessions: concrete session records (Section 5 data model). In Phase 1
 *               this is empty/absent, so we generate mock sessions from the
 *               recurring location patterns. In Phase 2 the section injects
 *               real records read from the per-location metafields, and the
 *               mock generator is simply never reached.
 *
 * SESSION RECORD SHAPE (matches plan Section 5):
 *   { id, locationId, date:"YYYY-MM-DD", time:"HH:MM", capacity, seatsLeft, boughtOut }
 *   id example: "union-boat-2026-07-03-1830"
 * ======================================================================== */
(function () {
  'use strict';

  /* ----- small date helpers (local time; weeks start Monday) ------------- */
  function startOfDay(d) { var x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
  function addDays(d, n) { var x = startOfDay(d); x.setDate(x.getDate() + n); return x; }
  function startOfWeek(d) {            // Monday 00:00 of d's week
    var x = startOfDay(d);
    var dow = (x.getDay() + 6) % 7;    // 0 = Monday ... 6 = Sunday
    return addDays(x, -dow);
  }
  function isoDate(d) {                 // "YYYY-MM-DD" in local time
    var x = startOfDay(d);
    var m = String(x.getMonth() + 1).padStart(2, '0');
    var day = String(x.getDate()).padStart(2, '0');
    return x.getFullYear() + '-' + m + '-' + day;
  }
  function parseISO(s) { var p = s.split('-'); return new Date(+p[0], +p[1] - 1, +p[2]); }
  function sameDay(a, b) { return isoDate(a) === isoDate(b); }
  // Exact start datetime of a session (local time). Note (plan §5 / Phase 5): dates
  // and times are the location's local (NZ) wall-clock; fine for local customers.
  function sessionStart(s) { var d = s.date.split('-'), t = s.time.split(':'); return new Date(+d[0], +d[1] - 1, +d[2], +t[0], +t[1]); }

  var DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  var DOW_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  function fmtDayShort(d) { return DOW[d.getDay()] + ' ' + d.getDate() + ' ' + MON[d.getMonth()]; }
  function fmtDayLong(d) { return DOW_LONG[d.getDay()] + ' ' + d.getDate() + ' ' + MON[d.getMonth()] + ' ' + d.getFullYear(); }
  // End time from a start "HH:MM" + duration (minutes); null if either is missing.
  function endTimeStr(time, dur) {
    if (!time || !(+dur > 0)) return null;
    var p = time.split(':'); var m = (+p[0]) * 60 + (+p[1]) + (+dur);
    m = ((m % 1440) + 1440) % 1440;
    return String(Math.floor(m / 60)).padStart(2, '0') + ':' + String(m % 60).padStart(2, '0');
  }
  // 12-hour clock formatter ("17:30" -> "5:30pm"). Warmer/friendlier than 24h
  // (redesign §4.2). Toggled per-widget by config.timeFormat ('12h' | '24h');
  // module-level so the standalone helpers can read it (one widget per page).
  var CLOCK12 = true;
  function fmtClock(t) {
    if (!t || !CLOCK12) return t;
    var p = t.split(':'), h = +p[0], m = p[1];
    var ap = h < 12 ? 'am' : 'pm', h12 = h % 12; if (h12 === 0) h12 = 12;
    return h12 + ':' + m + ap;
  }
  // "5:30–6:30pm" when a duration is known, else just "5:30pm".
  function fmtTimeRange(s) { var e = endTimeStr(s.time, s.duration); return fmtClock(s.time) + (e ? '–' + fmtClock(e) : ''); }

  /* ----- deterministic pseudo-random from a string (stable mock data) ---- */
  function hash01(str, salt) {
    var s = str + '|' + (salt || '');
    var h = 2166136261;
    for (var i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
    return ((h >>> 0) % 100000) / 100000;            // 0..1
  }

  /* ----- defaults: used only if the section forgets to inject config ----- */
  var DEFAULT_CONFIG = {
    // Launch: one location (Union Boat Club). `days` are JS weekday numbers
    // (0=Sun..6=Sat). `variants` are placeholder Shopify variant GIDs wired in Phase 3.
    // The `buyout` variant/type is now surfaced everywhere as "Group Booking"; the
    // internal key + `_ticket_type` value stay 'buyout' so the cart→order→Worker seam
    // is untouched (see the Worker's extractBookings).
    locations: [
      { id: 'union-boat',  name: 'In town, by the Awa',  days: [5],    variants: { single: 'REPLACE_SINGLE_UNION',      buyout: 'REPLACE_BUYOUT_UNION' } }
    ],
    times: ['17:30', '18:30', '19:30', '20:30'],
    capacity: 6,                             // seats in a public (individual) session
    groupMax: 8,                             // max people in a Group Booking (a bit closer together)
    // Launch (friends) prices; `standardPrices` are the full prices shown struck through.
    // Display only — the Shopify variant is the source of truth (set to the launch price
    // with compare-at = standard). Launch deal ends 6 Aug 2026.
    prices: { single: 15, buyout: 85 },
    standardPrices: { single: 23, buyout: 110 },
    windowDays: 140,                         // ~20 weeks ahead (browsable window)
    defaultView: 'date',                     // launch: single schedule view (no location picker)
    timeFormat: '12h',                       // '12h' (friendly default, redesign §4.2) | '24h'
    // "Things to know" block shown in the drawer at the decision point (P2/P5).
    // Richtext HTML (Shopify `richtext` setting) — paragraphs/lists/bold/links,
    // rendered via innerHTML (see buildDrawer()), not plain text.
    included: '<p>50 minute Sauna. Sessions run on time. Bring two towels, togs and a water bottle.</p>',
    firstTimeUrl: '/pages/faq',              // "Got questions?" expander link → FAQ
    termsUrl: '/pages/terms',   // T&Cs link on the drawer's "Checkout now" tick box
    termsAccepted: false,       // cart attribute "Terms accepted" == Yes (Liquid seeds it; /cart.js confirms)
    specialSessionsUrl: '/pages/special-sessions', // event-night details (anchored per theme)
    // Worker waitlist endpoint for "Notify me" on sold-out/bought-out sessions (P6).
    // Built in the NEXT backend; until then the front-end captures + the Worker stores.
    waitlistUrl: 'https://sauna-booking-webhook.finn-a-boyle.workers.dev/waitlist',
    // "Pay it forward" voluntary contribution (funds Gold Coin sessions). Amounts map to
    // the "Pay it forward" product's variant ids; opt-in surfaces in the booking drawer.
    payitforward: {
      amounts: [ { amount: 5, id: 49040992436480 }, { amount: 10, id: 49040992502016 }, { amount: 20, id: 49040992534784 } ],
      learnUrl: '/pages/special-sessions'
    },
    // Pass credits in the drawer. Logged-in holders get "Use my pass" ticked by default;
    // logged-out visitors see a "Got a pass? Log in" link. Balance + redeem go through the
    // App Proxy (/apps/passes → Worker /proxy/passes, signed with the logged-in customer).
    customerLoggedIn: false,
    passesUrl: '/apps/passes',
    passLoginUrl: '/account/login?return_url=%2Fpages%2Fbook'
  };

  /* ----- Phase 1 mock session generator ---------------------------------
   * Builds concrete records from each location's recurring weekly pattern,
   * from today out to the booking window. Availability is seeded from the id
   * so a given session always shows the same state across reloads. */
  function generateMockSessions(config, today) {
    var out = [];
    var end = addDays(today, config.windowDays);
    config.locations.forEach(function (loc) {
      for (var d = startOfDay(today); d < end; d = addDays(d, 1)) {
        if (loc.days.indexOf(d.getDay()) === -1) continue;
        config.times.forEach(function (time) {
          var id = loc.id + '-' + isoDate(d) + '-' + time.replace(':', '');
          var r = hash01(id, 'state');
          var cap = config.capacity;
          var seatsLeft, boughtOut = false;
          if (r < 0.08) { boughtOut = true; seatsLeft = 0; }      // ~8% bought out
          else if (r < 0.18) { seatsLeft = 0; }                   // ~10% sold out
          else if (r < 0.45) { seatsLeft = 1 + Math.floor(hash01(id, 'n') * 2); } // limited (1-2)
          else if (r < 0.72) { seatsLeft = 3 + Math.floor(hash01(id, 'n') * (cap - 3)); } // partial
          else { seatsLeft = cap; }                               // full availability
          out.push({ id: id, locationId: loc.id, date: isoDate(d), time: time, duration: 60, capacity: cap, seatsLeft: seatsLeft, boughtOut: boughtOut, tags: [] });
        });
      }
    });
    return out;
  }

  /* ----- availability semantics (shared by both views & the drawer) ------ */
  function availability(session) {
    if (session.boughtOut) return { state: 'boughtout', label: 'Booked out' };
    if (session.seatsLeft <= 0) return { state: 'soldout', label: 'Sold out' };
    if (session.seatsLeft <= 2) return { state: 'limited', label: session.seatsLeft + (session.seatsLeft === 1 ? ' spot left' : ' spots left') };
    return { state: 'available', label: session.seatsLeft + ' spots left' };
  }
  // Buyout is only offered when the whole session is still free.
  function buyoutAvailable(session) { return !session.boughtOut && session.seatsLeft === session.capacity; }

  /* ----- tiny DOM helper ------------------------------------------------- */
  function el(tag, attrs, children) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'class') n.className = attrs[k];
      else if (k === 'text') n.textContent = attrs[k];
      else if (k === 'html') n.innerHTML = attrs[k];
      else if (k.slice(0, 2) === 'on' && typeof attrs[k] === 'function') n.addEventListener(k.slice(2), attrs[k]);
      else if (attrs[k] != null) n.setAttribute(k, attrs[k]);
    });
    (children || []).forEach(function (c) { if (c != null) n.appendChild(typeof c === 'string' ? document.createTextNode(c) : c); });
    return n;
  }
  function seatDots(session) {
    var wrap = el('span', { class: 'sauna-seats', 'aria-hidden': 'true' });
    for (var i = 0; i < session.capacity; i++) {
      wrap.appendChild(el('span', { class: 'sauna-seat-dot' + (i < (session.capacity - session.seatsLeft) ? ' is-taken' : '') }));
    }
    return wrap;
  }
  // Special-session labels (e.g. "fem-only", "aromatherapy") as small badges.
  function tagBadges(session) {
    if (!session.tags || !session.tags.length) return null;
    return el('span', { class: 'sauna-tags' }, session.tags.map(function (t) { return el('span', { class: 'sauna-tag', text: t }); }));
  }
  // Only surfaced when a session runs longer/shorter than the standard 1hr slot.
  function durationBadge(session) {
    if (!session.duration || session.duration === 60) return null;
    return el('span', { class: 'sauna-chip-duration', text: session.duration + 'min' });
  }

  /* ======================================================================
   *  Widget instance
   * ==================================================================== */
  function SaunaBooking(root) {
    this.root = root;
    this.config = this.readJSON('[data-sauna-config]') || DEFAULT_CONFIG;
    // normalise: ensure every config key exists even if a partial config was injected
    Object.keys(DEFAULT_CONFIG).forEach(function (k) { if (this.config[k] == null) this.config[k] = DEFAULT_CONFIG[k]; }, this);
    CLOCK12 = (this.config.timeFormat || '12h') !== '24h';   // friendly 12h display by default

    // One tick for the whole visit: the cart attribute "Terms accepted" is the
    // single record, shared with the cart's tick box (snippets/cart-summary.liquid)
    // via window.SaunaCart.setTermsAccepted(). Seed from Liquid, confirm from
    // /cart.js (in case the page came from a cache), and follow changes made in
    // the cart drawer.
    this.termsAccepted = !!this.config.termsAccepted;
    var selfT = this;
    document.addEventListener('sauna-cart:terms', function (e) { selfT.termsAccepted = !!(e.detail && e.detail.accepted); });
    if (typeof fetch === 'function') {
      fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (c) { if (c && c.attributes) selfT.termsAccepted = c.attributes['Terms accepted'] === 'Yes'; })
        .catch(function () {});
    }

    // Pass credits for a logged-in customer (null = none / not logged in / legacy
    // single-use codes only). The drawer offers to use them by default.
    this.pass = null;
    if (this.config.customerLoggedIn && typeof fetch === 'function') {
      fetch(this.config.passesUrl + '/balance', { headers: { 'Accept': 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (d) {
          if (!d || !d.ok || !d.loggedIn || !((d.maxPerRedeem || 1) > 1)) return;
          var b = d.balance || {};
          if (!((b.public || 0) > 0 || (b.holds || []).length)) return;
          selfT.pass = { available: b.public || 0, holds: b.holds || [] };
        })
        .catch(function () {});
    }

    this.today = startOfDay(new Date());
    var injected = this.normalizeSessions(this.readJSON('[data-sauna-sessions]'));
    this.sessions = injected.length ? injected : generateMockSessions(this.config, this.today);
    this.usingMock = !injected.length;

    // Open on the first upcoming week that actually has sessions, so customers
    // don't page through empty weeks to reach our launch dates. (Full sessions
    // still count — we just skip weeks with nothing scheduled.) Falls back to the
    // current week when nothing is scheduled ahead.
    // (A session counts until it ENDS, so on a Sunday night after the last session we
    // open on next week, not on a week that's just finished.)
    var nowMs = Date.now(), firstDate = null;
    this.sessions.forEach(function (s) {
      var ends = sessionStart(s).getTime() + (+s.duration || 60) * 60000;
      if (ends > nowMs && (firstDate === null || s.date < firstDate)) firstDate = s.date;
    });
    var openWeek = firstDate ? startOfWeek(parseISO(firstDate)) : startOfWeek(this.today);

    // Launch: a single schedule (week) view — no location picker, no view toggle.
    this.state = {
      view: 'date',
      locationId: this.config.locations[0] ? this.config.locations[0].id : null,
      weekStart: openWeek
    };
    this.windowEnd = addDays(this.today, this.config.windowDays);
    this.locById = {};
    this.config.locations.forEach(function (l) { this.locById[l.id] = l; }, this);

    this.build();
    this.render();
  }

  SaunaBooking.prototype.readJSON = function (sel) {
    var n = this.root.querySelector(sel);
    if (!n) return null;
    try { return JSON.parse(n.textContent); } catch (e) { console.warn('[sauna-booking] bad JSON in', sel, e); return null; }
  };

  /* Accept either injected shape and return a flat, locationId-tagged array:
   *   - Phase 1 / mock : already a flat array (each record carries locationId).
   *   - Phase 2 metafields : an object { "<locationId>": [ {id,date,time,capacity,
   *     seatsLeft,boughtOut}, ... ] }. The per-location metafield (plan §5) stores
   *     no locationId per record, so we tag it here from the key. This is the whole
   *     adaptation needed to switch from mock data to real metafield data. */
  SaunaBooking.prototype.normalizeSessions = function (injected) {
    if (!injected) return [];
    if (Array.isArray(injected)) return injected;
    var out = [];
    Object.keys(injected).forEach(function (locId) {
      (injected[locId] || []).forEach(function (s) {
        var rec = {}; for (var k in s) if (Object.prototype.hasOwnProperty.call(s, k)) rec[k] = s[k];
        rec.locationId = rec.locationId || locId;
        out.push(rec);
      });
    });
    return out;
  };

  /* ----- one-time chrome (toggle, view container, drawer, toast) --------- */
  SaunaBooking.prototype.build = function () {
    this.root.classList.add('sauna-booking');
    if (this.usingMock) this.root.classList.add('sauna-booking--mock');

    this.viewEl = el('div', { class: 'sauna-view' });

    // Mock-data banner so nobody mistakes Phase 1 demo data for the real schedule.
    var banner = this.usingMock
      ? el('p', { class: 'sauna-mock-note', text: 'Demo schedule (Phase 1 mock data) — sessions and availability are generated for layout review only.' })
      : null;

    if (banner) this.root.appendChild(banner);
    this.root.appendChild(this.viewEl);
    this.buildDrawer();
    this.initStickyOffset();
  };

  /* The mobile week-nav sticks (see the position:sticky rule in the CSS). It must
   * sit just below the site's own sticky header, whose height isn't exposed as a
   * CSS var — so measure it and publish `--sauna-stick-top`. Falls back to 0 when
   * the header isn't sticky on this viewport (nothing to clear). Recompute on
   * resize so it stays correct across breakpoints / orientation changes. */
  SaunaBooking.prototype.updateStickyOffset = function () {
    var top = 0;
    var header = document.querySelector('.shopify-section-group-header-group');
    if (header) {
      var cs = window.getComputedStyle(header);
      if (cs.position === 'sticky' || cs.position === 'fixed') {
        top = Math.max(0, header.getBoundingClientRect().height + (parseFloat(cs.top) || 0));
      }
    }
    this.root.style.setProperty('--sauna-stick-top', top + 'px');
  };
  SaunaBooking.prototype.initStickyOffset = function () {
    var self = this;
    this.updateStickyOffset();
    window.addEventListener('resize', function () { self.updateStickyOffset(); });
  };

  SaunaBooking.prototype.render = function () {
    this.viewEl.innerHTML = '';
    this.renderByDate();
  };

  /* ======================================================================
   *  Schedule view — one week at a time, with week nav
   * ==================================================================== */
  SaunaBooking.prototype.renderByDate = function () {
    var self = this;
    var weekStart = this.state.weekStart;
    var weekEnd = addDays(weekStart, 6);

    var canPrev = startOfWeek(this.today) < weekStart;          // don't page before this week
    var canNext = addDays(weekStart, 7) < this.windowEnd;       // stay inside the booking window

    var nav = el('div', { class: 'sauna-weeknav' }, [
      el('button', { class: 'sauna-weeknav-btn', type: 'button', text: '‹ Prev week', disabled: canPrev ? null : 'disabled' }),
      el('span', { class: 'sauna-weeknav-range', text: rangeLabel(weekStart, weekEnd) }),
      el('button', { class: 'sauna-weeknav-btn', type: 'button', text: 'Next week ›', disabled: canNext ? null : 'disabled' })
    ]);
    nav.children[0].addEventListener('click', function () { if (canPrev) { self.state.weekStart = addDays(weekStart, -7); self.render(); } });
    nav.children[2].addEventListener('click', function () { if (canNext) { self.state.weekStart = addDays(weekStart, 7); self.render(); } });
    this.viewEl.appendChild(nav);

    var grid = el('div', { class: 'sauna-grid' });
    for (var i = 0; i < 7; i++) {
      var day = addDays(weekStart, i);
      var inWindow = day >= this.today && day < this.windowEnd;
      var cell = el('div', { class: 'sauna-grid-day' + (sameDay(day, this.today) ? ' is-today' : '') + (inWindow ? '' : ' is-outside') });
      cell.appendChild(el('div', { class: 'sauna-grid-date' }, [
        el('span', { class: 'sauna-grid-dow', text: DOW[day.getDay()] }),
        el('span', { class: 'sauna-grid-num', text: String(day.getDate()) })
      ]));

      var daySessions = inWindow ? this.sessionsOnDay(day) : [];
      if (!daySessions.length) {
        cell.appendChild(el('div', { class: 'sauna-grid-empty', text: inWindow ? '—' : '' }));
      } else {
        var stack = el('div', { class: 'sauna-grid-sessions' });
        daySessions.forEach(function (s) { stack.appendChild(self.gridSession(s)); });
        cell.appendChild(stack);
      }
      grid.appendChild(cell);
    }
    this.viewEl.appendChild(grid);
  };

  // All sessions (both locations) on a given day, sorted by time then location.
  SaunaBooking.prototype.sessionsOnDay = function (day) {
    var iso = isoDate(day), now = new Date();
    return this.sessions
      .filter(function (s) { return s.date === iso && sessionStart(s) >= now; })
      .sort(function (a, b) { return a.time === b.time ? a.locationId.localeCompare(b.locationId) : a.time.localeCompare(b.time); });
  };

  SaunaBooking.prototype.gridSession = function (s) {
    var self = this, av = availability(s);
    var bookable = !s.boughtOut && s.seatsLeft > 0;
    var durMinutes = s.duration && s.duration !== 60 ? ', ' + s.duration + ' minutes' : '';
    var chip = el('button', {
      class: 'sauna-chip sauna-state--' + av.state + (bookable ? '' : ' sauna-chip--waitlist'), type: 'button',
      'aria-label': fmtClock(s.time) + durMinutes + ', ' + av.label + (bookable ? '' : ' — get notified if a spot opens') + (s.tags && s.tags.length ? ', ' + s.tags.join(', ') : '')
    }, [
      el('span', { class: 'sauna-chip-time-row' }, [
        el('span', { class: 'sauna-chip-time', text: fmtClock(s.time) }),
        durationBadge(s)
      ]),
      tagBadges(s),
      el('span', { class: 'sauna-chip-avail', text: bookable ? av.label : av.label + ' · Notify me' })
    ]);
    chip.addEventListener('click', function () { if (bookable) self.openDrawer(s); else self.openWaitlist(s); });
    return chip;
  };

  /* ======================================================================
   *  Shared booking drawer
   * ==================================================================== */
  SaunaBooking.prototype.buildDrawer = function () {
    var self = this;
    this.overlay = el('div', { class: 'sauna-overlay', hidden: 'hidden' });
    this.drawer = el('aside', { class: 'sauna-drawer', role: 'dialog', 'aria-modal': 'true', 'aria-label': 'Book a session', hidden: 'hidden', tabindex: '-1' });
    this.overlay.addEventListener('click', function () { self.closeDrawer(); });
    document.addEventListener('keydown', function (e) {
      if (self.drawer.hidden) return;
      if (e.key === 'Escape') { self.closeDrawer(); return; }
      if (e.key === 'Tab') self.trapFocus(e);     // keep keyboard focus inside the open dialog (a11y)
    });
    this.root.appendChild(this.overlay);
    this.root.appendChild(this.drawer);

    this.toast = el('div', { class: 'sauna-toast', role: 'status', 'aria-live': 'polite', hidden: 'hidden' });
    this.root.appendChild(this.toast);
  };

  SaunaBooking.prototype.openDrawer = function (session) {
    var self = this;
    // Remember what opened the drawer so focus can return there on close (a11y).
    this._lastFocus = (document.activeElement && document.activeElement.focus) ? document.activeElement : null;
    var loc = this.locById[session.locationId];
    var av = availability(session);
    var canBuyout = buyoutAvailable(session);
    var maxSeats = session.seatsLeft;

    var groupMax = this.config.groupMax || session.capacity;

    // Selection state local to this drawer instance.
    var sel = { type: 'single', qty: maxSeats > 0 ? 1 : 0 };

    var qtyValue = el('span', { class: 'sauna-qty-value', text: String(sel.qty) });

    // Pass credits (see passBox below). `passSpare` = seats the cart's existing pass code
    // covers that no seat in the cart is using yet (filled in when the cart is read).
    sel.usePass = false;
    var passSpare = 0;
    function passUsable() { return self.pass ? self.pass.available + passSpare : 0; }
    function passUnits() {
      if (!self.pass || !sel.usePass || sel.type !== 'single') return 0;
      return Math.max(0, Math.min(sel.qty, passUsable()));
    }
    function priceLine() {
      var total = sel.type === 'buyout' ? self.config.prices.buyout : self.config.prices.single * (sel.qty - passUnits());
      return '$' + total;
    }
    var totalEl = el('strong', { class: 'sauna-drawer-total', text: priceLine() });

    // Launch price shown with the standard price struck through (the "was" price).
    function priceTag(kind) {
      var now = self.config.prices[kind];
      var was = self.config.standardPrices ? self.config.standardPrices[kind] : null;
      var kids = [el('span', { class: 'sauna-price-now', text: '$' + now })];
      if (was && was !== now) kids.push(el('s', { class: 'sauna-price-was', 'aria-label': 'usually $' + was, text: '$' + was }));
      return el('span', { class: 'sauna-ticket-price' }, kids);
    }

    function setType(t) {
      sel.type = t;
      single.classList.toggle('is-selected', t === 'single');
      buyout.classList.toggle('is-selected', t === 'buyout');
      qtyRow.style.display = t === 'single' ? '' : 'none';
      phoneRow.style.display = t === 'buyout' ? '' : 'none';
      refreshPass();
      totalEl.textContent = priceLine();
    }
    function setQty(q) {
      sel.qty = Math.max(1, Math.min(maxSeats, q));
      qtyValue.textContent = String(sel.qty);
      refreshPass();
      totalEl.textContent = priceLine();
    }

    var single = el('button', { class: 'sauna-ticket is-selected', type: 'button' }, [
      el('span', { class: 'sauna-ticket-name', text: 'A single spot' }),
      priceTag('single'),
      el('span', { class: 'sauna-ticket-sub', text: av.state === 'soldout' || av.state === 'boughtout' ? 'Unavailable' : 'Join others by the fire' })
    ]);
    var buyout = el('button', { class: 'sauna-ticket', type: 'button', disabled: canBuyout ? null : 'disabled' }, [
      el('span', { class: 'sauna-ticket-name', text: 'Group Booking' }),
      priceTag('buyout'),
      el('span', { class: 'sauna-ticket-sub', text: canBuyout ? 'The whole session, up to ' + groupMax + ' — just your mates' : 'Opens up when the whole session is still free — or pick a quieter time' })
    ]);
    single.addEventListener('click', function () { setType('single'); });
    if (canBuyout) buyout.addEventListener('click', function () { setType('buyout'); });

    var qtyRow = el('div', { class: 'sauna-qty' }, [
      el('span', { class: 'sauna-qty-label', text: 'Spots' }),
      el('button', { class: 'sauna-qty-btn', type: 'button', text: '−', 'aria-label': 'Fewer spots', onclick: function () { setQty(sel.qty - 1); } }),
      qtyValue,
      el('button', { class: 'sauna-qty-btn', type: 'button', text: '+', 'aria-label': 'More spots', onclick: function () { setQty(sel.qty + 1); } })
    ]);

    // "Use my pass" — ticked by default for a logged-in customer with credits, so a pass
    // holder can book straight from the schedule without visiting the "Your passes" panel.
    // Unticking pays as normal. Covers single spots only (not Group Booking). If they pick
    // more spots than they have credits, the pass covers what it can and they pay the rest.
    // Logged-out visitors get a "Got a pass? Log in" link instead.
    var passBox = null, passCheck = null, passMsg = null, passLogin = null;
    function plural(n, one, many) { return n + ' ' + (n === 1 ? one : many); }
    function refreshPass() {
      if (passLogin) passLogin.style.display = sel.type === 'single' ? '' : 'none';
      if (!passBox) return;
      passBox.style.display = sel.type === 'single' ? '' : 'none';
      var usable = passUsable(), k = passUnits();
      passCheck.disabled = usable < 1;
      if (usable < 1) passMsg.textContent = 'Your pass sessions are already in your cart.';
      else if (!sel.usePass) passMsg.textContent = 'You have ' + plural(usable, 'session', 'sessions') + ' on your pass. Tick to use ' + (usable === 1 ? 'it' : 'them') + '.';
      else if (k < sel.qty) passMsg.textContent = 'Covers ' + k + ' of ' + sel.qty + ' spots. You\u2019ll pay for ' + (sel.qty - k) + '.';
      else passMsg.textContent = 'Covers ' + plural(k, 'spot', 'spots') + '. ' + plural(usable - k, 'session', 'sessions') + ' left on your pass after this.';
    }
    if (this.pass) {
      sel.usePass = true;
      passCheck = el('input', { class: 'sauna-pass-box', type: 'checkbox' });
      passCheck.checked = true;
      passCheck.addEventListener('change', function () { sel.usePass = passCheck.checked; refreshPass(); totalEl.textContent = priceLine(); });
      passMsg = el('span', { class: 'sauna-pass-msg', 'aria-live': 'polite' });
      passBox = el('label', { class: 'sauna-pass' }, [passCheck, el('span', { class: 'sauna-pass-text' }, [el('strong', { text: 'Use my pass' }), passMsg])]);
      // What the cart's pass code already covers (from an earlier booking or the
      // "Your passes" panel), so the counts are right before they press anything.
      this.cartPassState().then(function (st) { passSpare = st.spare; refreshPass(); totalEl.textContent = priceLine(); }).catch(function () {});
    } else if (!this.config.customerLoggedIn && this.config.passLoginUrl) {
      passLogin = el('a', { class: 'sauna-pass-login', href: this.config.passLoginUrl, text: 'Got a pass? Log in to use it \u25B8' });
    }

    // Group Booking asks for a mobile number so we can reach the organiser about the
    // whole session (individual bookings don't need it). Rides through as a line-item
    // property; required before a Group Booking can be added to the cart.
    var phoneInput = el('input', { class: 'sauna-phone-input', type: 'tel', name: 'group_phone', autocomplete: 'tel', inputmode: 'tel', placeholder: 'Your mobile number', 'aria-label': 'Your mobile number' });
    var phoneRow = el('div', { class: 'sauna-phone', style: 'display:none' }, [
      el('label', { class: 'sauna-phone-label', text: 'Mobile number (so we can reach you about the session)' }),
      phoneInput
    ]);

    // Group Booking needs a mobile number before either CTA can proceed —
    // shared by "Add to cart" and "Checkout now" below.
    function requireGroupPhone() {
      if (sel.type !== 'buyout') return true;
      var phone = phoneInput.value.trim();
      if (!phone) {
        phoneInput.setAttribute('aria-invalid', 'true');
        phoneInput.focus();
        self.showToast('Please add a mobile number for your Group Booking.');
        return false;
      }
      phoneInput.removeAttribute('aria-invalid');
      sel.phone = phone;
      return true;
    }

    var cta = el('button', { class: 'sauna-drawer-cta', type: 'button' }, [
      el('span', { text: 'Add to cart — ' }), totalEl
    ]);
    cta.addEventListener('click', function () {
      if (!requireGroupPhone()) return;
      sel.passUnits = passUnits();
      self.addToCart(session, sel, { termsAccepted: termsBox.checked });
    });

    // "Checkout now" skips the cart drawer and takes the customer straight to
    // checkout with this session in it (same "buy it now" pattern as a normal
    // Shopify product page — adds the line then redirects, doesn't clear the cart).
    // It skips the cart, and the cart is where the terms tick box lives
    // (snippets/cart-summary.liquid), so this button needs its own tick box.
    // The box is only REQUIRED for "Checkout now", but whatever state it's in
    // when either button is pressed is saved to the cart, so the customer
    // never has to tick it twice.
    var termsBox = el('input', { class: 'sauna-terms-box', type: 'checkbox' });
    termsBox.checked = this.termsAccepted;
    var termsRow = el('label', { class: 'sauna-terms' }, [
      termsBox,
      el('span', { html: 'I have read and agree to the <a href="' + this.config.termsUrl + '" target="_blank" rel="noopener">Terms &amp; Conditions</a>' })
    ]);
    var checkoutCta = el('button', { class: 'sauna-drawer-cta sauna-drawer-cta--secondary', type: 'button', text: 'Checkout now' });
    checkoutCta.addEventListener('click', function () {
      if (!requireGroupPhone()) return;
      if (!termsBox.checked) {
        termsBox.setAttribute('aria-invalid', 'true');
        termsBox.focus();
        self.showToast('Please tick the box to agree to our Terms & Conditions.');
        return;
      }
      termsBox.removeAttribute('aria-invalid');
      sel.passUnits = passUnits();
      self.addToCart(session, sel, { checkout: true });
    });
    termsBox.addEventListener('change', function () { termsBox.removeAttribute('aria-invalid'); });

    this.drawer.innerHTML = '';
    this.drawer.appendChild(el('button', { class: 'sauna-drawer-close', type: 'button', 'aria-label': 'Close', text: '✕', onclick: function () { self.closeDrawer(); } }));
    // Event nights (fem-only, silent, nude-friendly, …) link through to their
    // section on the Special Sessions page so people know what they're booking.
    function slugify(t) { return String(t).toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''); }
    var tagLinks = (session.tags && session.tags.length)
      ? el('p', { class: 'sauna-drawer-tags' }, session.tags.map(function (t) {
          return el('a', { class: 'sauna-drawer-taglink', href: self.config.specialSessionsUrl + '#' + slugify(t), text: 'About ' + t + ' sessions ▸' });
        }))
      : null;

    this.drawer.appendChild(el('div', { class: 'sauna-drawer-head' }, [
      el('h2', { class: 'sauna-drawer-title', text: loc.name }),
      el('p', { class: 'sauna-drawer-when', text: fmtDayLong(parseISO(session.date)) + ' · ' + fmtTimeRange(session) }),
      el('p', { class: 'sauna-drawer-avail sauna-state--' + av.state, text: av.label }),
      tagBadges(session),
      tagLinks
    ]));
    // Reassurance at the highest-anxiety moment (P2/P5, redesign §4.3): a short
    // "what's included" line + an inline "first time?" expander, right where the
    // decision is made. `included` is a DRAFT placeholder until the team sets it.
    var inclDraft = /CONFIRM/i.test(this.config.included);
    var incl = el('div', { class: 'sauna-drawer-incl' + (inclDraft ? ' is-draft' : '') }, [
      el('strong', { class: 'sauna-drawer-incl-label', text: 'Things to know' }),
      // `included` is merchant-authored richtext from the theme editor (trusted
      // admin content, not user input), so it's safe to inject as HTML here.
      el('div', { class: 'sauna-drawer-incl-body', html: this.config.included })
    ]);
    var firstTime = el('a', {
      class: 'sauna-firsttime-link', href: this.config.firstTimeUrl, target: '_blank', rel: 'noopener',
      text: 'Got questions? Read our FAQs ▸'
    });

    // Themed nights (silent / women's / men's / queer / …) can't be bought out as a
    // Group Booking — only individual spots. Hide the Group Booking ticket for them.
    var isThemed = !!(session.tags && session.tags.length);

    // "Pay it forward" opt-in — a voluntary contribution that funds Gold Coin sessions.
    // Tap an amount chip to add it (tap again to remove); the "i" reveals an explainer.
    // The chosen contribution variant id is stored on sel.contribution (null = none).
    sel.contribution = null;
    var pif = null;
    var pifCfg = this.config.payitforward;
    if (pifCfg && pifCfg.amounts && pifCfg.amounts.length) {
      var pifChips = pifCfg.amounts.map(function (a) {
        var b = el('button', { class: 'sauna-pif-chip', type: 'button', text: '$' + a.amount });
        b.addEventListener('click', function () {
          var wasOn = b.classList.contains('is-on');
          pifChips.forEach(function (x) { x.classList.remove('is-on'); });
          if (wasOn) { sel.contribution = null; }
          else { b.classList.add('is-on'); sel.contribution = a.id; }
        });
        return b;
      });
      var pifCard = el('div', { class: 'sauna-pif-card', html:
        'Pay it forward by contributing to our Gold Coin sessions — giving more people a low-cost sauna experience. ' +
        '<a href="' + pifCfg.learnUrl + '">Learn about Gold Coin sessions ▸</a>' });
      pifCard.hidden = true;
      var pifInfo = el('button', { class: 'sauna-pif-info', type: 'button', 'aria-label': 'About pay it forward', 'aria-expanded': 'false', text: 'i' });
      pifInfo.addEventListener('click', function () {
        pifCard.hidden = !pifCard.hidden;
        pifInfo.setAttribute('aria-expanded', pifCard.hidden ? 'false' : 'true');
      });
      pif = el('div', { class: 'sauna-pif' }, [
        el('div', { class: 'sauna-pif-head' }, [
          el('span', { class: 'sauna-pif-label', text: 'Pay it forward' }),
          pifInfo
        ]),
        el('div', { class: 'sauna-pif-chips' }, pifChips),
        pifCard
      ]);
    }

    var body = el('div', { class: 'sauna-drawer-body' }, [
      incl,
      el('p', { class: 'sauna-drawer-legend', text: 'Choose your spot' }),
      el('div', { class: 'sauna-tickets' }, isThemed ? [single] : [single, buyout]),
      qtyRow,
      passBox,
      passLogin,
      phoneRow,
      pif,
      firstTime
    ]);
    this.drawer.appendChild(body);
    this.drawer.appendChild(el('div', { class: 'sauna-drawer-foot' }, [cta, termsRow, checkoutCta]));

    setType('single');
    if (maxSeats > 0) setQty(1);

    this.overlay.hidden = false;
    this.drawer.hidden = false;
    // Guard: if the drawer was closed before this frame ran (browsers pause
    // frames in background tabs), don't re-open a hidden drawer.
    requestAnimationFrame(function () { if (self.drawer.hidden) return; self.drawer.classList.add('is-open'); self.overlay.classList.add('is-open'); });
    this.drawer.focus();
  };

  SaunaBooking.prototype.closeDrawer = function () {
    var self = this;
    this.drawer.classList.remove('is-open');
    this.overlay.classList.remove('is-open');
    setTimeout(function () {
      self.drawer.hidden = true; self.overlay.hidden = true;
      // Return focus to the session row/chip that opened the drawer (a11y).
      if (self._lastFocus && self._lastFocus.focus) { try { self._lastFocus.focus(); } catch (e) {} }
    }, 200);
  };

  /* Keep Tab/Shift+Tab cycling within the open drawer so keyboard users can't
   * tab into the page behind the modal (WCAG 2.4.3 / focus management). */
  SaunaBooking.prototype.trapFocus = function (e) {
    var sel = 'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    var nodes = Array.prototype.filter.call(this.drawer.querySelectorAll(sel), function (n) { return n.offsetParent !== null; });
    if (!nodes.length) return;
    var first = nodes[0], last = nodes[nodes.length - 1], active = document.activeElement;
    if (e.shiftKey && (active === first || active === this.drawer)) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && active === last) { e.preventDefault(); first.focus(); }
  };

  /* ----- Waitlist "Notify me" (P6, redesign §5) --------------------------
   * Sold-out / bought-out sessions open this instead of the booking drawer.
   * Captures minimal contact (name + email/phone — P3 minimal fields) and POSTs
   * to the Worker /waitlist endpoint, which stores demand against book:<sessionId>
   * in KV and can auto-notify the first in line when a refund frees a seat (the
   * Worker already restores seats on cancel/refund). Falls back to a friendly
   * toast in preview / if the endpoint isn't reachable. */
  SaunaBooking.prototype.openWaitlist = function (session) {
    var self = this;
    this._lastFocus = (document.activeElement && document.activeElement.focus) ? document.activeElement : null;
    var loc = this.locById[session.locationId];
    var av = availability(session);

    var name = el('input', { class: 'sauna-wl-input', type: 'text', name: 'name', autocomplete: 'name', placeholder: 'Your name', 'aria-label': 'Your name' });
    var contact = el('input', { class: 'sauna-wl-input', type: 'email', name: 'contact', autocomplete: 'email', placeholder: 'Email — for the heads-up', 'aria-label': 'Email or phone', required: 'required' });
    var msg = el('p', { class: 'sauna-wl-msg', hidden: 'hidden', 'aria-live': 'polite' });
    var submit = el('button', { class: 'sauna-drawer-cta', type: 'submit', text: 'Notify me if a spot opens' });

    var form = el('form', { class: 'sauna-wl-form' }, [name, contact, msg, submit]);
    form.addEventListener('submit', function (e) { e.preventDefault(); self.submitWaitlist(session, { name: name.value.trim(), contact: contact.value.trim() }, msg, submit); });

    this.drawer.innerHTML = '';
    this.drawer.appendChild(el('button', { class: 'sauna-drawer-close', type: 'button', 'aria-label': 'Close', text: '✕', onclick: function () { self.closeDrawer(); } }));
    this.drawer.appendChild(el('div', { class: 'sauna-drawer-head' }, [
      el('h2', { class: 'sauna-drawer-title', text: loc.name }),
      el('p', { class: 'sauna-drawer-when', text: fmtDayLong(parseISO(session.date)) + ' · ' + fmtTimeRange(session) }),
      el('p', { class: 'sauna-drawer-avail sauna-state--' + av.state, text: av.label })
    ]));
    this.drawer.appendChild(el('div', { class: 'sauna-drawer-body' }, [
      el('p', { class: 'sauna-wl-lead', text: 'This one’s full. Leave your details and we’ll let you know the moment a spot opens — first in, first served.' }),
      form
    ]));

    this.overlay.hidden = false; this.drawer.hidden = false;
    // Guard: if the drawer was closed before this frame ran (browsers pause
    // frames in background tabs), don't re-open a hidden drawer.
    requestAnimationFrame(function () { if (self.drawer.hidden) return; self.drawer.classList.add('is-open'); self.overlay.classList.add('is-open'); });
    this.drawer.focus();
  };

  SaunaBooking.prototype.submitWaitlist = function (session, data, msgEl, submitEl) {
    var self = this;
    if (!data.contact) { msgEl.hidden = false; msgEl.textContent = 'Pop in an email or phone so we can reach you.'; return; }
    var payload = { sessionId: session.id, locationId: session.locationId, date: session.date, time: session.time, name: data.name, contact: data.contact };
    var url = this.config.waitlistUrl;
    if (!url || typeof fetch !== 'function') {
      console.info('[sauna-booking] (demo) would POST waitlist:', payload);
      this.showToast('You’re on the list ✓ (demo preview)'); this.closeDrawer(); return;
    }
    submitEl.disabled = true; submitEl.setAttribute('aria-busy', 'true');
    fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) })
      .then(function (r) { if (!r.ok) throw new Error('HTTP ' + r.status); return r.json().catch(function () { return {}; }); })
      .then(function () { self.showToast('You’re on the list ✓ We’ll be in touch if a spot opens.'); self.closeDrawer(); })
      .catch(function (err) {
        console.error('[sauna-booking] waitlist failed', err);
        msgEl.hidden = false; msgEl.textContent = 'Sorry — that didn’t save. Please try again.';
        submitEl.disabled = false; submitEl.removeAttribute('aria-busy');
      });
  };

  /* ----- Add to cart (Phase 3 write path) --------------------------------
   * POSTs the line item to Shopify's /cart/add.js with the session encoded in
   * line-item properties. The Cloudflare Worker reads `_session_id` from the
   * resulting order-created webhook and decrements that session's seatsLeft.
   *
   * Fallback: if the variant id is still a placeholder (standalone preview) or
   * there's no fetch (jsdom/tests), it logs the payload and toasts instead of
   * posting — so the preview keeps working without a Shopify cart.
   *
   * `opts.checkout` (the drawer's "Checkout now" button): after a successful add,
   * redirect straight to /checkout instead of opening the cart drawer — same
   * "buy it now" pattern as a normal Shopify product page (adds to whatever's
   * already in the cart, doesn't clear it). */
  SaunaBooking.prototype.addToCart = function (session, sel, opts) {
    var self = this;
    opts = opts || {};
    var loc = this.locById[session.locationId];
    var isBuyout = sel.type === 'buyout';
    var variantId = isBuyout ? loc.variants.buyout : loc.variants.single;
    var quantity = isBuyout ? 1 : sel.qty;
    var properties = {
      'Session': loc.name + ' — ' + fmtDayLong(parseISO(session.date)) + ' ' + fmtTimeRange(session),
      '_session_id': session.id,        // the Worker decrements this session's seatsLeft
      '_ticket_type': sel.type          // 'single' | 'buyout' (buyout = a Group Booking)
    };
    // Group Booking carries the organiser's mobile so we can reach them about the session.
    if (isBuyout && sel.phone) properties['Phone'] = sel.phone;
    // Special-session labels (fem-only, silent, …) — visible on cart & order.
    if (session.tags && session.tags.length) properties['Session type'] = session.tags.join(', ');

    var isPlaceholder = !variantId || String(variantId).indexOf('REPLACE') === 0;
    if (isPlaceholder || typeof fetch !== 'function') {
      console.info('[sauna-booking] (demo) would POST /cart/add.js:', { id: variantId, quantity: quantity, properties: properties });
      this.showToast((isBuyout ? 'Group Booking' : quantity + ' seat' + (quantity === 1 ? '' : 's')) +
        ' selected · (demo preview — ' + (opts.checkout ? 'checkout' : 'cart') + ' wiring is live on the store)');
      this.closeDrawer();
      return;
    }

    // Add the session line; if the customer opted to "pay it forward", add the
    // contribution as a second line (the Worker tallies it by the _contribution prop).
    var items = [{ id: Number(variantId) || variantId, quantity: quantity, properties: properties }];
    if (sel.contribution) {
      items.push({ id: Number(sel.contribution) || sel.contribution, quantity: 1, properties: { '_contribution': 'payitforward' } });
    }

    var ctas = this.drawer.querySelectorAll('.sauna-drawer-cta');
    ctas.forEach(function (b) { b.disabled = true; b.setAttribute('aria-busy', 'true'); });
    var reenable = function () { ctas.forEach(function (b) { b.disabled = false; b.removeAttribute('aria-busy'); }); };

    var postCart = function () {
      return fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      }).then(function (r) {
        if (!r.ok) return r.json().then(function (e) { throw new Error(e && (e.description || e.message) || ('HTTP ' + r.status)); });
        return r.json();
      });
    };

    // Pass credits: put a pass code covering these seats on the cart FIRST, so the
    // theme's own cart refresh after the add already shows them at $0. If that fails,
    // add nothing — never silently charge someone who expected their pass to be used.
    var units = isBuyout ? 0 : (sel.passUnits || 0);
    var prep = units > 0 ? self.applyPass(units) : Promise.resolve();
    var passFail = function (err) {
      console.error('[sauna-booking] pass not applied', err);
      self.showToast('Sorry \u2014 couldn\u2019t use your pass just now. Nothing was added. Try again, or untick \u201CUse my pass\u201D.');
      reenable();
    };

    if (opts.checkout) {
      // Straight to checkout — no need for the native cart-drawer event dance
      // below, we're navigating away immediately.
      // Record the tick-box acceptance on the cart (it shows on the order as
      // "Terms accepted: Yes", same attribute the cart page's tick box sets).
      self.termsAccepted = true;
      prep.then(function () {
        return postCart()
          .then(function () {
            return fetch('/cart/update.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({ attributes: { 'Terms accepted': 'Yes' } })
            });
          })
          .then(function () { window.location.href = '/checkout'; })
          .catch(function (err) {
            console.error('[sauna-booking] checkout add failed', err);
            self.showToast('Sorry — couldn’t start checkout. Please try again.');
            reenable();
          });
      }, passFail);
      return;
    }

    if (typeof opts.termsAccepted === 'boolean') self.termsAccepted = opts.termsAccepted;
    prep.then(function () {
      return self.notifyThemeCart(items, postCart, opts.termsAccepted)
        .then(function () {
          self.showToast(units > 0 ? 'Added to cart ✓ ' + (units === 1 ? '1 spot' : units + ' spots') + ' on your pass' : 'Added to cart ✓');
          self.closeDrawer();
        })
        .catch(function (err) {
          console.error('[sauna-booking] add to cart failed', err);
          self.showToast('Sorry — couldn’t add that to the cart. Please try again.');
          reenable();
        });
    }, passFail);
  };

  /* ----- Pass credits on the cart -----------------------------------------
   * One pass code per cart: two codes would both discount the same seats. So each
   * booking that uses the pass TOPS UP the cart's code: the Worker issues one code for
   * (seats the old code covered + the new ones) and deletes the old. Codes are minted
   * on the pass-redemption Discount Function, which frees at most that many Single
   * Seats; any other seats in the cart are charged. Holds last 2 hours. */
  SaunaBooking.prototype.cartPassState = function () {
    var self = this;
    var singles = this.config.locations.map(function (l) { return String(l.variants.single); });
    return fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('cart HTTP ' + r.status); return r.json(); })
      .then(function (c) {
        var codes = (c.discount_codes || []).map(function (d) { return String(d.code); });
        var holds = (self.pass && self.pass.holds) || [];
        var mine = null;
        codes.forEach(function (code) {
          holds.forEach(function (h) { if (h.bucket === 'public' && String(h.code).toUpperCase() === code.toUpperCase()) mine = h; });
        });
        var seats = 0;
        (c.items || []).forEach(function (i) { if (singles.indexOf(String(i.variant_id)) >= 0) seats += i.quantity; });
        return { codes: codes, mine: mine, seats: seats, spare: mine ? Math.max(0, mine.qty - seats) : 0 };
      });
  };

  SaunaBooking.prototype.applyPass = function (units) {
    var self = this;
    return this.cartPassState().then(function (st) {
      var extra = units - st.spare;
      if (extra <= 0) return; // the cart's code already has room for these seats
      var total = (st.mine ? st.mine.qty : 0) + extra;
      return fetch(self.config.passesUrl + '/redeem', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ bucket: 'public', qty: total, replace: st.mine ? st.mine.code : '' })
      })
        .then(function (r) { return r.json(); })
        .then(function (d) {
          if (!d || !d.ok || !d.code) throw new Error((d && d.error) || 'redeem failed');
          if (d.balance) { self.pass.available = d.balance.public || 0; self.pass.holds = d.balance.holds || []; }
          // Keep any other codes; drop older pass codes (dead once replaced).
          var keep = st.codes.filter(function (c) { return !/^SAUNA-/i.test(c); });
          keep.push(d.code);
          return fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ discount: keep.join(',') })
          }).then(function (r) { if (!r.ok) throw new Error('discount HTTP ' + r.status); });
        });
    });
  };

  /* Hand the add to window.SaunaCart (theme/assets/sauna-cart.js), which
   * dispatches Shopify's CartLinesUpdateEvent the way Horizon's own product
   * form does, then checks the cart icon/drawer really refreshed (and repairs
   * them if not). Read that file's header before changing any of this.
   *
   * ⚠ Don't bring back `import('@shopify/events')` here. Shopify's CDN minifier
   * turns it into `require(...)` in this classic script, which silently broke
   * the cart refresh in production. That's why the logic lives in a real ES
   * module.
   *
   * If the module never loads, we still add the line, then send the customer
   * to /cart so they always see the right cart (and log loudly). */
  function whenSaunaCart(timeoutMs) {
    if (window.SaunaCart) return Promise.resolve(window.SaunaCart);
    return new Promise(function (resolve, reject) {
      var t = setTimeout(function () {
        document.removeEventListener('sauna-cart:ready', onReady);
        reject(new Error('sauna-cart.js did not load'));
      }, timeoutMs);
      function onReady() { clearTimeout(t); resolve(window.SaunaCart); }
      document.addEventListener('sauna-cart:ready', onReady, { once: true });
    });
  }

  SaunaBooking.prototype.notifyThemeCart = function (items, run, termsAccepted) {
    return whenSaunaCart(4000).then(function (cart) {
      return cart.addLines(items, { source: 'sauna-booking', termsAccepted: termsAccepted });
    }, function (err) {
      console.error('[sauna-booking] ' + err.message + ' — adding without live cart refresh, then opening /cart', err);
      var saveTerms = typeof termsAccepted === 'boolean'
        ? fetch('/cart/update.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
            body: JSON.stringify({ attributes: { 'Terms accepted': termsAccepted ? 'Yes' : '' } })
          }).catch(function () {})
        : Promise.resolve();
      return saveTerms.then(run).then(function (added) {
        window.location.href = '/cart';
        return added;
      });
    });
  };

  SaunaBooking.prototype.showToast = function (msg) {
    var self = this;
    this.toast.textContent = msg;
    this.toast.hidden = false;
    requestAnimationFrame(function () { self.toast.classList.add('is-open'); });
    clearTimeout(this._toastT);
    this._toastT = setTimeout(function () {
      self.toast.classList.remove('is-open');
      setTimeout(function () { self.toast.hidden = true; }, 250);
    }, 3200);
  };

  /* ----- week range label, e.g. "30 Jun – 6 Jul" ------------------------- */
  function rangeLabel(a, b) {
    var left = a.getDate() + (a.getMonth() === b.getMonth() ? '' : ' ' + MON[a.getMonth()]);
    return left + ' – ' + b.getDate() + ' ' + MON[b.getMonth()];
  }

  /* ----- boot ------------------------------------------------------------ */
  function init() {
    document.querySelectorAll('[data-sauna-booking]').forEach(function (root) {
      if (root.__saunaBooked) return;          // guard against double-init (theme editor re-renders)
      root.__saunaBooked = true;
      try { new SaunaBooking(root); } catch (e) { console.error('[sauna-booking] failed to init', e); }
    });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Shopify theme editor: re-init when a section is reloaded.
  document.addEventListener('shopify:section:load', init);
})();
